<template>
  <div
    v-if="internalShow"
    :data-testid="testId"
    class="dp-survey-modal"
    role="dialog"
    aria-modal="true"
    :aria-label="title"
    @keydown.esc.stop.prevent="close"
  >
    <!-- Backdrop: click to dismiss (fail-open, never traps the user) -->
    <div class="dp-survey-modal__backdrop" @click="close"></div>

    <div ref="dialog" class="dp-survey-modal__dialog">
      <header class="dp-survey-modal__header">
        <h2 class="dp-survey-modal__title">{{ title }}</h2>
        <button ref="closeButton" type="button" class="dp-survey-modal__close" :aria-label="closeLabel" @click="close">
          <span aria-hidden="true">&times;</span>
        </button>
      </header>

      <div class="dp-survey-modal__body">
        <!-- src bound only once shown (NFR-3 lazy-load); load timeout + error both fail open. -->
        <!-- sandbox omits allow-top-navigation so the survey can never framebust the cart away (FR-3). -->
        <iframe
          v-if="iframeSrc"
          :src="iframeSrc"
          :title="title"
          class="dp-survey-modal__iframe"
          frameborder="0"
          sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
          @load="onIframeLoad"
          @error="onIframeError"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Self-hosted survey dialog (rendering mechanism R2 from TD-ENG-14333).
 *
 * Hosts a cross-origin SurveyMonkey survey in an iframe inside a dialog that we own:
 * desktop centered modal (420-520px, auto height) and mobile bottom sheet (~85vh).
 *
 * We do NOT attempt to detect submission or auto-close — the survey is cross-origin, so
 * the user closes the dialog explicitly. Session/suppression state is keyed on OPEN, not
 * submit (owned by the surveyTrigger util). This wrapper is fail-open: it is
 * keyboard-dismissible (ESC + visible close), returns focus to the trigger on close, and
 * suppresses itself rather than blocking checkout if the survey fails to load.
 */
const IFRAME_LOAD_TIMEOUT_MS = 8000;

export default {
  name: 'DpSurveySubscriptionModal',
  props: {
    surveyUrl: {
      type: String,
      required: true,
    },
    // Mirrored internally so the component can self-close without the parent.
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    closeLabel: {
      type: String,
      required: true,
    },
    // Stable hook for QA automation (see qa-handoff.md Testability Gaps).
    testId: {
      type: String,
      default: 'survey-subscription-modal',
    },
  },
  emits: ['close'],
  data() {
    return {
      internalShow: this.show,
      // Bind immediately when mounted already-open (the cart's dynamic-mount path) so the
      // survey is in the first render; otherwise stay null so page load pays no cost.
      iframeSrc: this.show ? this.surveyUrl : null,
      loadTimeoutId: null,
      previouslyFocused: null,
    };
  },
  watch: {
    show(value) {
      if (value) {
        this.internalShow = true;
        this.open();
        return;
      }
      // Parent-driven close: run cleanup (clear timeout, null iframe src, return focus)
      // rather than just flipping the flag.
      if (this.internalShow) {
        this.close();
      }
    },
  },
  mounted() {
    if (this.internalShow) {
      this.open();
    }
  },
  beforeDestroy() {
    this.clearLoadTimeout();
  },
  methods: {
    open() {
      this.previouslyFocused = document.activeElement;

      // Setting the src here (not on page load) keeps SurveyMonkey off the cart until a
      // removal actually triggers the survey (NFR-3).
      this.iframeSrc = this.surveyUrl;

      // Fail-open: if the survey never loads, suppress the dialog rather than leave a
      // broken/blocking overlay in front of checkout.
      this.clearLoadTimeout();
      this.loadTimeoutId = window.setTimeout(() => {
        this.close();
      }, IFRAME_LOAD_TIMEOUT_MS);

      this.$nextTick(() => {
        if (this.$refs.closeButton) {
          this.$refs.closeButton.focus();
        }
      });
    },
    onIframeLoad() {
      // A successful load cancels the fail-open timeout.
      this.clearLoadTimeout();
    },
    onIframeError() {
      // Fail open: close silently rather than surface a broken survey to the user.
      this.close();
    },
    close() {
      this.clearLoadTimeout();
      this.internalShow = false;
      this.iframeSrc = null;

      // Return focus to the cart control that opened the dialog (NFR-4).
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus();
      }
      this.previouslyFocused = null;

      this.$emit('close');
    },
    clearLoadTimeout() {
      if (this.loadTimeoutId) {
        window.clearTimeout(this.loadTimeoutId);
        this.loadTimeoutId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$dp-survey-z: 1100;

.dp-survey-modal {
  position: fixed;
  inset: 0;
  z-index: $dp-survey-z;
  display: flex;
  // Desktop default: center the dialog.
  align-items: center;
  justify-content: center;

  &__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
  }

  &__dialog {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 480px;
    max-width: calc(100vw - 2rem);
    max-height: calc(100vh - 4rem);
    background: #fff;
    border-radius: 0.75rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
    overflow: hidden;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;
  }

  &__title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 700;
    color: #1f2937;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
    color: #6b7280;
    background: transparent;
    border: 0;
    border-radius: 0.375rem;
    cursor: pointer;

    &:hover {
      color: #1f2937;
      background: #f3f4f6;
    }

    &:focus-visible {
      outline: 2px solid #2563eb;
      outline-offset: 2px;
    }
  }

  &__body {
    flex: 1 1 auto;
    min-height: 0;
    // Give the survey room on desktop; height is otherwise driven by this fixed area.
    height: 70vh;
    max-height: 640px;
  }

  &__iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
  }

  // Mobile: bottom sheet. Tall enough to actually use the survey (its form chrome +
  // question + answers + nav button need room), while staying a sheet with the backdrop
  // and close control visible above it.
  @media (max-width: 767.98px) {
    align-items: flex-end;
    justify-content: stretch;

    .dp-survey-modal__dialog {
      width: 100%;
      max-width: 100%;
      height: 85vh;
      max-height: 85vh;
      border-radius: 0.75rem 0.75rem 0 0;
    }

    .dp-survey-modal__body {
      height: auto;
      flex: 1 1 auto;
      max-height: none;
    }
  }
}
</style>
